import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Eye, EyeOff, Pencil } from 'lucide-react';
import VaultOnboarding from './VaultOnboarding';
// Trusted-device helpers removed; login is OTP-only without device trust persistence

const WHITELISTED_EMAILS = [
  'admin@griswoldvault.com',
  'family@griswold.com',
  'patriarch@griswold.com',
  'matriarch@griswold.com',
  'admin@griswold.com'
];

export default function LoginForm() {
  // Derive slug similar to GroupContext: treat reserved paths as non-slugs
  const slug = React.useMemo(() => {
    const firstSeg = (window.location.pathname.split('/').filter(Boolean)[0] || '').trim();
    const reserved = new Set(['auth', 'login', 'reset-password', 'code', 'preview']);
    return firstSeg && !reserved.has(firstSeg.toLowerCase()) ? firstSeg : '';
  }, []);

  const [email, setEmail] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Removed Google OAuth (simplifies to email OTP only)
  // OTP-only auth flow
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  // Device trust removed

  // Short, security-focused error mapper (e.g., Supabase OTP rate limit)
  const friendlyAuthError = React.useCallback((msg: string | undefined | null) => {
    const raw = String(msg || '').trim();
    const m = raw.match(/For security purposes, you can only request this after\s+(\d+)\s+seconds?\.?/i);
    if (m) {
      const seconds = parseInt(m[1], 10);
      const s = isNaN(seconds) ? '' : `${seconds}s`;
      return s ? `For security, try again in ${s}.` : 'For security, try again shortly.';
    }
    if (/too many requests/i.test(raw)) {
      return 'Too many attempts — try again shortly.';
    }
    return raw || 'Something went wrong. Please try again.';
  }, []);

  // Check if this is a password reset redirect
  React.useEffect(() => {
    const checkForPasswordReset = async () => {
      // Supabase uses URL hash fragments for auth redirects: #access_token=xxx&type=recovery
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      const accessToken = hashParams.get('access_token');

      if (type === 'recovery') {
        console.log('Password reset detected - checking session validity');

        try {
          // Verify we have a valid session for password reset
          const { data: { session }, error } = await supabase.auth.getSession();

          console.log('Session check:', { hasSession: !!session, error, type });

          if (error || !session || !accessToken) {
            console.error('Invalid or expired password reset session:', error);
            setError('Password reset link is invalid or expired. Please request a new one.');
            // Clear the invalid URL
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          }

          console.log('Valid password reset session found - showing reset form');
          setShowNewPassword(true);
          setSuccess('Password reset verified. Please enter your new password.');

        } catch (error) {
          console.error('Session check failed:', error);
          setError('Unable to verify password reset. Please try requesting a new reset link.');
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } else if (type === 'magiclink' || type === 'signup') {
        console.log('Magic link or signup detected - this should auto-login via App.tsx');
        // Don't show reset form for magic links
      }
    };

    checkForPasswordReset();
  }, []); const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Handle password reset
    if (showResetPassword) {

      try {
        // Use the correct Supabase method for password reset
        const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
          // Send users to our dedicated password reset route; App.tsx will render PasswordResetPage
          redirectTo: `${window.location.origin}/auth/reset-password#recovery=true`,
        });
        if (error) throw error;
        setSuccess('Password reset email sent! Check your inbox and click the link to reset your password.');
        setShowResetPassword(false);
        setResetEmail('');
      } catch (error: any) {
        setError(friendlyAuthError(error?.message));
      }
      setLoading(false);
      return;
    }

    // Handle new password submission
    if (showNewPassword) {
      if (newPassword !== confirmPassword) {
        setError('Passwords do not match.');
        setLoading(false);
        return;
      }

      if (newPassword.length < 6) {
        setError('Password must be at least 6 characters long.');
        setLoading(false);
        return;
      }

      try {
        // First, make sure we have a valid session from the password reset link
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          setError('Session expired. Please request a new password reset link.');
          setLoading(false);
          return;
        }

        // Update the password using the recovery session
        const { error: updateError } = await supabase.auth.updateUser({
          password: newPassword
        });

        if (updateError) {
          // If session is invalid, show helpful error
          if (updateError.message.includes('session') || updateError.message.includes('expired')) {
            setError('Password reset link has expired. Please request a new one.');
          } else {
            setError(updateError.message);
          }
          setLoading(false);
          return;
        }

        setSuccess('Password updated successfully! You are now signed in.');
        setShowNewPassword(false);
        setNewPassword('');
        setConfirmPassword('');
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);

      } catch (error: any) {
        console.error('Password update error:', error);
        if (error.message.includes('session') || error.message.includes('expired')) {
          setError('Password reset session has expired. Please request a new password reset link.');
        } else {
          setError(error.message);
        }
      }
      setLoading(false);
      return;
    }

    // OTP-only flow: if code not sent, send it; else verify it
    try {
      if (!otpSent) {
        const normalized = email.trim().toLowerCase();
        const { error } = await supabase.auth.signInWithOtp({
          email: normalized,
          options: {
            shouldCreateUser: true,
          },
        });
        if (error) throw error;
        setSuccess('Secure code sent to your email.');
        setOtpSent(true);
      } else {
        // verify OTP code (6-digit)
        const normalized = email.trim().toLowerCase();
        const code = otpCode.trim();
        const { error } = await supabase.auth.verifyOtp({
          email: normalized,
          token: code,
          type: 'email',
        });
        if (error) throw error;

        // Device trust removed
        // Mark intent to create a vault when landing at root
        if (!slug) {
          try { localStorage.setItem('gv:pendingCreateVault', '1'); } catch { }
        }
        // Cache pretty vault name for slug paths
        try {
          const firstSeg = (window.location.pathname.split('/').filter(Boolean)[0] || '').trim();
          const reserved = new Set(['auth', 'login', 'reset-password', 'code', 'preview']);
          const slug = firstSeg && !reserved.has(firstSeg.toLowerCase()) ? firstSeg : '';
          if (slug && !localStorage.getItem(`gv:vaultName:${slug}`)) {
            const friendly = slug.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
            localStorage.setItem(`gv:vaultName:${slug}`, friendly);
          }
        } catch { }
      }
    } catch (error: any) {
      setError(friendlyAuthError(error?.message));
    }
    setLoading(false);
  }

  // Show onboarding flow for new users
  if (showOnboarding) {
    return <VaultOnboarding onComplete={() => setShowOnboarding(false)} />;
  }

  // Google OAuth removed

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto mb-8">
            <img
              src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
              alt="Vault Contact Logo"
              className="h-24 w-24 mx-auto object-contain"
            />
          </div>
          <h2 className="text-5xl font-light text-white mb-1 tracking-wide leading-tight">
            <span className="block">VAULT</span>
            <span className="block">CONTACT</span>
          </h2>
          <div className="h-px w-32 bg-gradient-to-r from-transparent via-gray-400 to-transparent mx-auto mb-4"></div>
          <div className="mt-4 inline-flex items-center px-4 py-2 rounded-sm bg-gray-800/80 border border-gray-700/50 backdrop-blur-sm">
            <div className="w-2 h-2 bg-green-400 rounded-full mr-3 animate-pulse"></div>
            <span className="text-gray-300 text-sm font-medium tracking-wide">SECURE CONNECTION</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div className="relative bg-black/40 backdrop-blur-xl rounded-none border border-gray-800/50 p-8 shadow-2xl ring-1 ring-gray-800/60">
            {/* Secure Handshake indicator (visual only) */}
            {loading && !otpSent && (
              <div className="mb-5">
                <div className="h-1 w-full bg-gray-900/80 overflow-hidden rounded">
                  <div className="h-full w-1/2 bg-gradient-to-r from-gray-500 via-gray-300 to-gray-500 animate-pulse"></div>
                </div>
                <p className="mt-2 text-xs text-gray-500 text-center tracking-wide">Establishing secure handshake…</p>
              </div>
            )}
            {error && (
              <div className="mb-6 p-4 bg-red-900/30 border border-red-800/50 backdrop-blur-sm">
                <p className="text-red-300 text-sm font-medium">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-green-900/30 border border-green-800/50 backdrop-blur-sm">
                <p className="text-green-300 text-sm font-medium">{success}</p>
              </div>
            )}

            {/* Password Reset Form */}
            {showResetPassword && (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-white mb-2">RESET PASSWORD</h3>
                  <p className="text-gray-400 text-sm">Enter your authorized email to receive a password reset link.</p>
                </div>

                <div>
                  <label htmlFor="resetEmail" className="block text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
                    Authorized Email
                  </label>
                  <input
                    id="resetEmail"
                    name="resetEmail"
                    type="email"
                    required
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-4 py-4 border border-gray-700/50 bg-gray-900/50 text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 focus:bg-gray-900/80 transition-all duration-300 text-sm font-medium"
                    placeholder="Enter your authorized email"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-8 px-4 py-4 bg-white text-black font-semibold hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed tracking-wide text-sm"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-3"></div>
                      SENDING RESET EMAIL...
                    </div>
                  ) : (
                    'SEND RESET EMAIL'
                  )}
                </button>

                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPassword(false);
                      setError('');
                      setSuccess('');
                      setResetEmail('');
                    }}
                    className="text-gray-400 hover:text-gray-300 text-xs font-medium transition-colors"
                  >
                    Back to sign in
                  </button>
                </div>
              </div>
            )}

            {/* New Password Form */}
            {showNewPassword && (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-white mb-2">SET NEW PASSWORD</h3>
                  <p className="text-gray-400 text-sm">Enter your new password below.</p>
                  <p className="text-gray-500 text-xs mt-2">
                    Note: Password reset links expire after 1 hour for security.
                  </p>
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
                    New Password
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-4 border border-gray-700/50 bg-gray-900/50 text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 focus:bg-gray-900/80 transition-all duration-300 text-sm font-medium"
                    placeholder="Enter new password"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-4 border border-gray-700/50 bg-gray-900/50 text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 focus:bg-gray-900/80 transition-all duration-300 text-sm font-medium"
                    placeholder="Confirm new password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-8 px-4 py-4 bg-white text-black font-semibold hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed tracking-wide text-sm"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-3"></div>
                      UPDATING PASSWORD...
                    </div>
                  ) : (
                    'UPDATE PASSWORD'
                  )}
                </button>
              </div>
            )}

            {/* Regular Login/Signup Form */}
            {!showResetPassword && !showNewPassword && (
              <>
                <div className="space-y-4">

                  {!otpSent ? (
                    <div>
                      <label htmlFor="email" className="block text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
                        Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-4 border border-gray-700/50 bg-gray-900/50 text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 focus:bg-gray-900/80 transition-all duration-300 text-sm font-medium"
                        placeholder="Enter your email"
                      />
                      <p className="mt-2 text-xs text-gray-400">One‑time encrypted access code.</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</div>
                          <div className="mt-1 text-gray-200 text-sm font-medium break-all">{email}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setOtpSent(false); setOtpCode(''); setSuccess(''); }}
                          className="p-1.5 rounded-md text-gray-400 hover:text-gray-100 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-600"
                          aria-label="Use a different email"
                          title="Use a different email"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                      <div>
                        <label htmlFor="otp" className="block text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
                          Authentication Code
                        </label>
                        <div className="relative">
                          <input
                            id="otp"
                            name="otp"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={6}
                            required
                            value={otpCode}
                            onChange={(e) => {
                              const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                              setOtpCode(v);
                              setError('');
                            }}
                            onPaste={(e) => {
                              try {
                                const txt = e.clipboardData?.getData('text') || '';
                                const m = txt.match(/\b(\d{6})\b/);
                                if (m) {
                                  e.preventDefault();
                                  setOtpCode(m[1]);
                                  setError('');
                                }
                              } catch { /* ignore */ }
                            }}
                            onFocus={async () => {
                              try {
                                if ((otpCode || '').length) return;
                                const txt = await navigator.clipboard.readText();
                                const m = txt.match(/\b(\d{6})\b/);
                                if (m) {
                                  setOtpCode(m[1]);
                                  setError('');
                                }
                              } catch { /* permissions may block; ignore */ }
                            }}
                            className="w-full px-4 pr-10 py-4 border border-gray-700/50 bg-gray-900/50 text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 focus:bg-gray-900/80 transition-all duration-300 text-sm font-medium tracking-widest"
                            placeholder="Enter 6-digit code"
                          />
                          {Boolean((otpCode || '').length) && (
                            <button
                              type="button"
                              onClick={() => setOtpCode('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 px-1"
                              aria-label="Clear code"
                              title="Clear"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        {/* Device trust removed */}
                        <p className="text-xs text-gray-500 mt-2">Enter the code sent to your email.</p>
                      </div>
                    </>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-8 px-4 py-4 bg-gray-900 text-white font-semibold border border-gray-700 hover:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed tracking-wide text-sm"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-3"></div>
                      {otpSent ? 'VERIFYING ACCESS…' : 'REQUESTING SECURE ACCESS…'}
                    </div>
                  ) : (
                    otpSent ? 'Verify Secure Code' : 'Request Secure Access'
                  )}
                </button>

                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => setShowOnboarding(true)}
                    className="text-gray-400 hover:text-gray-300 text-xs font-medium transition-colors"
                  >
                    Need to create an account? Sign up
                  </button>
                  {/* Password reset disabled in OTP-only mode */}
                </div>
              </>
            )}

            {/* Minimal footer copy only */}
            <div className="mt-6 text-center"></div>
          </div>
        </form>

        <div className="text-center text-gray-500 text-xs">
          <p className="font-light tracking-wider">© 2025 VAULT CONTACT</p>
        </div>
      </div>
    </div>
  );
}